import { lazy } from 'react';
import { Route } from 'react-router-dom';
import { useFeatureEntitlements } from '@frontegg/react';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { useReferralNavMenus } from './referral/navMenus';
import { usePatientNavMenus } from './patient/navMenus';
import {
  usePhoneTriageNavMenus,
  usePhoneTriageFullAccess,
  useCallQaNavAccess,
} from './phoneTriage/navMenus';
import {
  useCareConnectSideMenus,
  useSettingsSideMenus,
} from 'src/components/SideMenus';
import { useAmbientSoloSideMenus } from 'src/modules/ambient_solo/SideMenus';
import { useCelloWidgetSideMenu } from 'src/modules/CelloWidget/SideMenu';
import { useSuperAdminSideMenu } from 'src/modules/SuperAdmin/SideMenus';
import { useTaskNavMenus } from './task/navMenus';
import { useTasksV2NavMenus } from './tasksV2/navMenus';
import {
  useDocumentsNavMenus,
  useDocumentsAccess,
} from './documents/navMenus';
import { ButtonProps } from '@insight-health-ai/roseate/components';

export type AppNavMenu = {
  path: string;
  title: React.ReactNode;
  tooltip?: React.ReactNode;
  icon: React.ReactNode;
  onClick?: () => void;
  showBadge?: 'dot';
  submenu?: Pick<AppNavMenu, 'icon' | 'title' | 'path'>[];
  tourPopup?: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    content: string;
    primaryAction?: ButtonProps;
    secondaryAction?: ButtonProps;
  };
};

const PatientRoutes = lazy(() => import('./patient'));
const ReferralRoutes = lazy(() => import('./referral'));
const PhoneTriageRoutes = lazy(() => import('./phoneTriage'));
const FrontdeskRoutes = lazy(() => import('./frontdesk'));
const TaskRoutes = lazy(() => import('./task'));
const TasksV2Routes = lazy(() => import('./tasksV2'));
const TagRoutes = lazy(() => import('./settings/tags'));
const DocumentsRoutes = lazy(() => import('./documents'));

export const useAppRoutes = () => {
  const { isSuperAdmin } = useGetCurrentPractitioner();
  const { isEntitled: isPatientsModuleEntitled } =
    useFeatureEntitlements('patients-module');
  const { isEntitled: isFrontdeskModuleEntitled } =
    useFeatureEntitlements('frontdesk');
  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');

  const { isEntitled: isTasksModuleEntitled } = useFeatureEntitlements('tasks');
  const { isEntitled: isTasksV2ModuleEntitled } =
    useFeatureEntitlements('tasks-v2');

  const isDocumentsEntitled = useDocumentsAccess();

  const canFullPhoneTriage = usePhoneTriageFullAccess();
  const canCallQaRoute = useCallQaNavAccess();
  const mountPhoneTriageRoutes = canFullPhoneTriage || canCallQaRoute;

  const isTagModuleEntitled =
    canFullPhoneTriage || isReferralsModuleEntitled || isSuperAdmin;

  return [
    isPatientsModuleEntitled && (
      <Route key="patients" path="/patients/*" element={<PatientRoutes />} />
    ),
    isReferralsModuleEntitled && (
      <Route key="referrals" path="/referrals/*" element={<ReferralRoutes />} />
    ),
    mountPhoneTriageRoutes && (
      <Route
        key="phone-triage"
        path="/phone-triage/*"
        element={<PhoneTriageRoutes />}
      />
    ),
    isFrontdeskModuleEntitled && (
      <Route
        key="frontdesk"
        path="/frontdesk/*"
        element={<FrontdeskRoutes />}
      />
    ),
    isTagModuleEntitled && (
      <Route key="tags" path="/settings/tags/*" element={<TagRoutes />} />
    ),
    isTasksModuleEntitled && (
      <Route key="tasks" path="/tasks/*" element={<TaskRoutes />} />
    ),
    isTasksV2ModuleEntitled && (
      <Route key="tasks-v2" path="/tasks-v2/*" element={<TasksV2Routes />} />
    ),
    isDocumentsEntitled && (
      <Route
        key="documents"
        path="/documents/*"
        element={<DocumentsRoutes />}
      />
    ),
  ].filter(Boolean);
};

export const useAppNavMenus = (): AppNavMenu[] => {
  const patientNavMenus = usePatientNavMenus();
  const referralNavMenus = useReferralNavMenus();
  const phoneTriageNavMenus = usePhoneTriageNavMenus();
  const taskNavMenus = useTaskNavMenus();
  const tasksV2NavMenus = useTasksV2NavMenus();
  const careConnectNavMenus = useCareConnectSideMenus();
  const ambientSoloNavMenus = useAmbientSoloSideMenus();
  const superAdminNavMenus = useSuperAdminSideMenu();
  const celloWidgetNavMenus = useCelloWidgetSideMenu();
  const documentsNavMenus = useDocumentsNavMenus();
  const settingsNavMenus = useSettingsSideMenus();

  return [
    ...patientNavMenus,
    ...referralNavMenus,
    ...phoneTriageNavMenus,
    ...taskNavMenus,
    ...tasksV2NavMenus,
    ...documentsNavMenus,
    ...careConnectNavMenus,
    ...ambientSoloNavMenus,
    ...superAdminNavMenus,
    ...celloWidgetNavMenus,
    ...settingsNavMenus,
  ];
};
